import React, { useState, useEffect } from "react";
import { ROLE_DISPLAY_NAMES } from "../utils/constants";

const OrganizationUsersGrid = ({ 
  providerId,
  orgId,
  providerData,
  isLoading,
  isError,
  onRoleChange,
  roleType = "sponsor", // "sponsor" or "vendor"
}) => {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userRoles, setUserRoles] = useState({});
  
  // `providerData` (with `contacts`) is passed from parent; parent handles fetching
  // `isLoading`/`isError` are also passed from parent for display purposes
  
  // We don't call the update API here immediately. Selections are stored
  // locally and the parent will perform the updates after dual-role creation.
  const isUpdating = false;

  // Define roles based on roleType prop
  const availableRoles = roleType === "vendor" 
    ? ["Vendor Admin", "RFI Manager - Approver", "RFI Manager - User", "VQA Manager"]
    : ["Sponsor Admin", "RFI Manager", "VQA Manager"];

  // Default role based on roleType
  const defaultRole = roleType === "vendor" ? "Vendor Admin" : "Sponsor Admin";

  // Extract users from the providerData passed by the parent
  const users = providerData?.contacts || [];
  
  console.log("Fetched users:", users);
  users.forEach((user) => {
    console.log("User clerk ID:", user.user_clerk_id, "for", user.firstName, user.lastName);
  });

  // Initialize selected users and roles when users data changes
  useEffect(() => {
    if (users && users.length > 0) {
      // All users are selected by default
      const initialSelectedUsers = users.map(user => user.contactId);
      setSelectedUsers(initialSelectedUsers);
      
      // Initialize roles for all users - always use the default role based on roleType
      // Don't use existing subRoles as they may be from a different role type (e.g., vendor roles when assigning sponsor roles)
      const initialRoles = {};
      users.forEach(user => {
        // Always use the default role for the current roleType (sponsor or vendor)
        initialRoles[user.contactId] = defaultRole;
      });
      setUserRoles(initialRoles);
      // Notify parent about initial selections and roles so it can perform
      // updates after dual-role creation. This ensures the parent has the
      // selected users list even if the user doesn't interact with the grid.
      if (onRoleChange) {
        users.forEach(user => {
          const role = initialRoles[user.contactId] || defaultRole;
          onRoleChange(user.contactId, role);
        });
      }
    } else {
      // Clear selections if no users
      setSelectedUsers([]);
      setUserRoles({});
    }
  }, [users, defaultRole]);

  // Handle checkbox toggle and inform parent about selection/deselection
  const handleUserToggle = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        // User is being deselected
        const next = prev.filter(id => id !== userId);
        // Notify parent that user is deselected
        if (onRoleChange) {
          onRoleChange(userId, null);
        }
        return next;
      } else {
        // User is being selected
        const next = [...prev, userId];
        // Use existing role or default role when selecting
        const roleToUse = userRoles[userId] || defaultRole;
        // Update local state if role wasn't set
        if (!userRoles[userId]) {
          setUserRoles(prevRoles => ({ ...prevRoles, [userId]: defaultRole }));
        }
        // Notify parent that user is selected with role
        if (onRoleChange) {
          onRoleChange(userId, roleToUse);
        }
        return next;
      }
    });
  };

  // Handle role change for a user — persist locally and notify parent. The
  // parent will perform API updates after dual-role creation completes.
  const handleRoleChange = (userId, newRole) => {
    // Update the role in local state
    setUserRoles(prevRoles => {
      const updatedRoles = { ...prevRoles, [userId]: newRole };
      return updatedRoles;
    });
    
    // Notify parent about role change (only if user is selected)
    if (selectedUsers.includes(userId) && onRoleChange) {
      onRoleChange(userId, newRole);
    }
  };

  // Handle "Select All" button
  const handleSelectAll = () => {
    const allUserIds = users.map(user => user.contactId);
    setSelectedUsers(allUserIds);
    
    // Notify parent about all selections with their roles
    if (onRoleChange) {
      users.forEach(user => {
        const role = userRoles[user.contactId] || defaultRole;
        onRoleChange(user.contactId, role);
      });
    }
  };

  // Handle "Deselect All" button
  const handleDeselectAll = () => {
    // Notify parent about all deselections
    if (onRoleChange) {
      selectedUsers.forEach(userId => {
        onRoleChange(userId, null);
      });
    }
    // Clear local state
    setSelectedUsers([]);
  };

  if (isLoading) {
    return (
      <div className="mt-6 border border-gray-200 rounded-lg p-4">
        <p className="text-sm text-gray-500">Loading users...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mt-6 border border-gray-200 rounded-lg p-4">
        <p className="text-sm text-red-600">Failed to load users</p>
      </div>
    );
  }

  if (!users || users.length === 0) {
    return null;
  }

  return (
    <div className="mt-6 border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-900">
          Select existing users to carry-over
        </h3>
        <div className="flex items-center gap-3">
          {selectedUsers.length < users.length && (
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-sm text-indigo-600 hover:text-indigo-500 font-medium"
            >
              Select All
            </button>
          )}
          {selectedUsers.length > 0 && (
            <button
              type="button"
              onClick={handleDeselectAll}
              className="text-sm text-indigo-600 hover:text-indigo-500 font-medium"
            >
              Deselect All
            </button>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {users.map((user) => {
          const userId = user.contactId;
          const isSelected = selectedUsers.includes(userId);
          const userName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
          const userEmail = user.email || '';

          return (
            <div
              key={userId}
              className="flex items-center justify-between py-3 border-b border-gray-200 last:border-b-0"
            >
              <div className="flex items-center min-w-0 flex-1">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleUserToggle(userId)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <div className="ml-3 min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {userName}
                  </p>
                  <p className="text-sm text-gray-500 truncate">{userEmail}</p>
                </div>
              </div>

              <div className="ml-4 flex-shrink-0 w-48">
                <select
                  value={userRoles[userId] || defaultRole}
                  onChange={(e) => handleRoleChange(userId, e.target.value)}
                  disabled={!isSelected || isUpdating}
                  className={`text-sm border-gray-300 rounded-md w-full ${
                    !isSelected || isUpdating ? "bg-gray-100 text-gray-400" : ""
                  }`}
                >
                  {availableRoles.map((role) => (
                    <option key={role} value={role}>
                      {ROLE_DISPLAY_NAMES[role] || role}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          );
        })}
      </div>

      {selectedUsers.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
          </p>
        </div>
      )}
    </div>
  );
};

export default OrganizationUsersGrid;
